import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  type DeletedWorkspace,
  useDeletedWorkspaces,
  usePurgeWorkspace,
  useRestoreWorkspace,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { formatDateTime } from '@/shared/lib/datetime';
import {
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Icons,
  LoadingState,
  TextField,
  useToast,
} from '@/shared/ui';

import styles from './WorkspaceTrashPage.module.css';

const DAY = 24 * 60 * 60 * 1000;

/** Whole days until ``purge_after``; 0 = less than a day. */
function daysLeft(purgeAfter: string, now = Date.now()): number {
  return Math.max(0, Math.floor((new Date(purgeAfter).getTime() - now) / DAY));
}

/**
 * The workspace trash: deleted team workspaces the viewer may still restore or remove for good.
 * Permanent deletion is irreversible and therefore needs the exact workspace name.
 */
export function WorkspaceTrashPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const deleted = useDeletedWorkspaces();
  const restore = useRestoreWorkspace();
  const [purging, setPurging] = useState<DeletedWorkspace | null>(null);
  const date = (iso: string) => formatDateTime(iso, i18n.language, zone);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <Link to="/app" className={styles.back}>
          <Icons.chevronLeft /> {t('workspaceTrash.page.back')}
        </Link>
        <h1 className={styles.title}>{t('workspaceTrash.page.title')}</h1>
        <p className={styles.subtitle}>{t('workspaceTrash.page.subtitle')}</p>
      </header>
      {deleted.isPending && <LoadingState />}
      {deleted.isError && (
        <ErrorState error={deleted.error} onRetry={() => void deleted.refetch()} />
      )}
      {deleted.isSuccess && deleted.data.length === 0 && (
        <EmptyState title={t('workspaceTrash.page.empty')} />
      )}
      {deleted.isSuccess && deleted.data.length > 0 && (
        <ul className={styles.list} aria-label={t('workspaceTrash.page.title')}>
          {deleted.data.map((workspace) => {
            const days = daysLeft(workspace.purge_after);
            return (
              <li key={workspace.id} className={styles.item} data-testid="trashed-workspace">
                <div className={styles.info}>
                  <h2 className={styles.name}>{workspace.name}</h2>
                  <p className={styles.meta}>
                    {workspace.owner_name &&
                      `${t('workspaceTrash.page.owner', { name: workspace.owner_name })} · `}
                    {t('workspaceTrash.page.boards', { count: workspace.board_count })}
                  </p>
                  <p className={styles.meta}>
                    {workspace.deleted_by_name
                      ? t('workspaceTrash.page.deletedBy', {
                          name: workspace.deleted_by_name,
                          date: date(workspace.deleted_at),
                        })
                      : t('workspaceTrash.page.deletedOn', { date: date(workspace.deleted_at) })}
                  </p>
                  <p className={styles.deadline}>
                    {t('workspaceTrash.page.deadline', { date: date(workspace.purge_after) })}
                    <span className={days <= 3 ? styles.urgent : styles.badge}>
                      {days === 0
                        ? t('workspaceTrash.page.lastDay')
                        : t('workspaceTrash.page.remaining', { count: days })}
                    </span>
                  </p>
                </div>
                <div className={styles.actions}>
                  <Button
                    disabled={restore.isPending}
                    onClick={() => {
                      restore.mutate(workspace.id, {
                        onSuccess: () => {
                          toast.show({ message: t('workspaceTrash.page.restored') });
                        },
                      });
                    }}
                  >
                    <Icons.refresh /> {t('workspaceTrash.page.restore')}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setPurging(workspace);
                    }}
                  >
                    <Icons.trash /> {t('workspaceTrash.page.purge')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <FormError error={restore.error} />
      {purging && (
        <PurgeDialog
          workspace={purging}
          onClose={() => {
            setPurging(null);
          }}
        />
      )}
    </div>
  );
}

function PurgeDialog({ workspace, onClose }: { workspace: DeletedWorkspace; onClose: () => void }) {
  const { t } = useTranslation();
  const purge = usePurgeWorkspace();
  const [name, setName] = useState('');
  return (
    <Dialog title={t('workspaceTrash.page.purgeTitle', { name: workspace.name })} onClose={onClose}>
      <form
        className={styles.dialog}
        onSubmit={(event) => {
          event.preventDefault();
          purge.mutate({ workspaceId: workspace.id, confirm_name: name }, { onSuccess: onClose });
        }}
      >
        <p className={styles.danger} role="alert">
          {t('workspaceTrash.page.purgeWarning')}
        </p>
        <TextField
          label={t('workspaceTrash.page.purgeConfirm')}
          value={name}
          autoFocus
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <FormError error={purge.error} />
        <div className={styles.dialogActions}>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="danger"
            disabled={name !== workspace.name || purge.isPending}
          >
            {t('workspaceTrash.page.purge')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
